import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";

export default function PaySuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", data: null, attempts: 0 });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", data: null, attempts: 0 });
      return;
    }
    let timer;
    const poll = async (attempts = 0) => {
      if (attempts >= 8) {
        setState({ status: "timeout", data: null, attempts });
        return;
      }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", data, attempts });
        } else if (data.status === "expired") {
          setState({ status: "expired", data, attempts });
        } else {
          setState({ status: "pending", data, attempts });
          timer = setTimeout(() => poll(attempts + 1), 2200);
        }
      } catch {
        setState({ status: "error", data: null, attempts });
      }
    };
    poll(0);
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <Layout>
      <section className="min-h-[60vh] flex items-center justify-center px-6 py-24">
        <div className="max-w-xl w-full text-center" data-testid="pay-result">
          {state.status === "paid" && (
            <>
              <CheckCircle2 size={64} className="mx-auto text-[#FF5A36] mb-6" />
              <div className="font-mono-label text-[#FF5A36] mb-3">· PAYMENT RECEIVED</div>
              <h1 className="font-display text-5xl tracking-tighter mb-4">Thanks — paid.</h1>
              <p className="text-[#495057]">
                Your payment of <strong>£{state.data?.amount?.toFixed(2)}</strong> has been received. A
                confirmation has been sent to your email.
              </p>
            </>
          )}
          {(state.status === "checking" || state.status === "pending") && (
            <>
              <Loader2 size={48} className="mx-auto text-[#0B1215] mb-6 animate-spin" />
              <div className="font-mono-label text-[#FF5A36] mb-3">· VERIFYING</div>
              <h1 className="font-display text-4xl tracking-tighter mb-4">Confirming your payment…</h1>
              <p className="text-[#495057]">This usually takes a few seconds.</p>
            </>
          )}
          {(state.status === "error" || state.status === "expired" || state.status === "timeout") && (
            <>
              <XCircle size={64} className="mx-auto text-[#0B1215] mb-6" />
              <div className="font-mono-label text-[#FF5A36] mb-3">· COULDN'T CONFIRM</div>
              <h1 className="font-display text-4xl tracking-tighter mb-4">Status not confirmed.</h1>
              <p className="text-[#495057]">
                Please check your email — if you've been charged, the booking will be marked paid
                automatically. Otherwise call 07346 812 685 and Rob will sort it.
              </p>
            </>
          )}
          <Link
            to="/"
            className="mt-10 inline-flex items-center gap-2 bg-[#0B1215] hover:bg-[#FF5A36] text-[#F8F9FA] px-6 py-3 font-mono-label transition-colors"
          >
            Back to home →
          </Link>
        </div>
      </section>
    </Layout>
  );
}
